import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	faSearch,
	faUser,
	faArrowLeft,
	faBolt,
	faChevronDown,
	faChevronRight,
	faClockRotateLeft,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { useSearch } from "@/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
	clearGlobalSearch,
	clearSelectedCompany,
	searchSelector,
	setSelectedCompany,
} from "@/pages/Private/redux/search/search.slice";
import {
	IPersonSummary,
	ListPersonGlobalSearchInfoDTO,
	OrganizationGlobalSearchInfoDTO,
} from "@/pages/Private/redux/search/search.schema";
import { useLazyGlobalSearchQuery } from "@/pages/Private/redux/search/search.api";
import { ContactLeadCompact } from "@/pages/Private/pages/Messages/components/LeadCardCompact/ContactLeadCompact";

import { SearchField } from "@/components/SearchField/SearchField";
import { Icon } from "@/components/Icon/Icon";
import { Loader } from "@/components/Loader/Loader";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { LinkedInPersonCard } from "./LinkedInPersonCard";
import { hasDisplayableCompany, isContactInCampaign, resolveDisplayCompany } from "./utils";
import {
	RECENT_SEARCH_ICONS,
	MAX_VISIBLE_SEARCHES,
	RecentSearch,
	clearStoredRecentSearches,
	loadRecentSearches,
	persistRecentSearches,
	pushRecentSearch,
} from "./recentSearches";

interface ByNameTabProps {
	selectedContactIds: number[];
	setSelectedContactIds: (ids: number[]) => void;
	selectedLinkedInItems: {
		linkedinUrl: string;
		name: string;
		domain?: string;
		companyEnrichId?: number;
	}[];
	setSelectedLinkedInItems: (
		items: { linkedinUrl: string; name: string; domain?: string; companyEnrichId?: number }[]
	) => void;
	onContactNavigate: (item: ListPersonGlobalSearchInfoDTO) => void;
	onNavigateToFilters: (prefilledCompany?: { name: string; domain: string }) => void;
	// Single-select (compose) mode hides the "Select all" bulk controls — selecting is one-at-a-time.
	singleSelect?: boolean;
	isModalOpened?: boolean;
}

export const ByNameTab: FunctionComponent<ByNameTabProps> = ({
	selectedContactIds,
	setSelectedContactIds,
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	onContactNavigate,
	onNavigateToFilters,
	singleSelect = false,
	isModalOpened = false,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	// The surrounding Modal is keepMounted, so a plain autoFocus on the input would only
	// fire on the very first mount — refocus imperatively every time the modal (re)opens.
	const searchInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isModalOpened) {
			searchInputRef.current?.focus();
		}
	}, [isModalOpened]);

	// 500ms debounce: every new character resets the timer, so the search only runs
	// once typing settles (see the fetch effect below for in-flight cancellation).
	const { searchValue, setSearchValue, query, setQuery, isTyping } = useSearch(undefined, 500);
	const [search, setSearch] = useState("");
	const [lastSearch, setLastSearch] = useState("");
	const [lastSearches, setLastSearches] = useState<RecentSearch[]>(loadRecentSearches);
	const [isLinkedInExpanded, setIsLinkedInExpanded] = useState(true);
	const [isContactsExpanded, setIsContactsExpanded] = useState(true);
	const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

	const [companiesSnapshot, setCompaniesSnapshot] = useState<OrganizationGlobalSearchInfoDTO[]>([]);
	const [contactsPerCompanySnapshot, setContactsPerCompanySnapshot] = useState<Map<number, number>>(
		new Map()
	);
	const [campaignCountsSnapshot, setCampaignCountsSnapshot] = useState<Map<number, number>>(
		new Map()
	);

	const {
		contacts: contactData,
		companyEnrich,
		organizations,
		selectedCompany,
		isLoading: isLoadingSearch,
	} = useAppSelector(searchSelector);

	const contactList = contactData.data;
	// Only people whose matched/current experience resolves to a company are shown;
	// the count badge and bulk-select operate on the same displayable set.
	const linkedInResults = companyEnrich.items.filter(hasDisplayableCompany);
	const totalCompanies = organizations?.data.length ?? 0;
	const totalLinkedIn = linkedInResults.length;
	// Idle is based on the immediate input so the results panel (with its spinner) shows
	// the moment the user types — rather than lingering on the idle/recent-searches screen.
	const isIdle = searchValue.trim().length < 2 && !selectedCompany;

	const isSelectedAllLinkedIn =
		linkedInResults.length > 0 &&
		linkedInResults.every((p) => selectedLinkedInItems.some((li) => li.companyEnrichId === p.id));
	const isSelectedAllContacts =
		contactList.length > 0 &&
		contactList.every(
			(c) => c?.prospect?.id !== undefined && selectedContactIds.includes(c?.prospect?.id)
		);
	const [fetchSearch, { isFetching }] = useLazyGlobalSearchQuery();
	// True from the keystroke until the debounced fetch fires. Folding it into the loading
	// states means the user sees one continuous spinner instead of a flash of "0 results".
	const isDebouncePending = isTyping && searchValue.trim().length >= 2;
	const isSearching = isFetching || isDebouncePending;

	const companies = selectedCompany
		? companiesSnapshot.filter((c) => c.id === selectedCompany.id)
		: (organizations?.data ?? []);

	const contactsPerCompany = useMemo(() => {
		const map = new Map<number, number>();

		for (const c of contactList) {
			const orgId = c.prospect?.organization?.id;

			if (!orgId) {
				continue;
			}

			map.set(orgId, (map.get(orgId) ?? 0) + 1);
		}

		return map;
	}, [contactList]);

	const displayedContactsPerCompany = selectedCompany
		? contactsPerCompanySnapshot
		: contactsPerCompany;

	// `query` is the debounced value from useSearch (500ms), so the search only
	// fires once typing settles — not on every keystroke. Company selection still
	// reacts immediately because selectedCompany is a separate dependency.
	// The cleanup aborts the in-flight request before the next one starts (and on
	// unmount), so a slower earlier response can never land after — and overwrite —
	// the results for what the user is currently typing.
	useEffect(() => {
		if (!selectedCompany && query.length < 2) {
			dispatch(clearGlobalSearch());

			return;
		}

		const request = fetchSearch({
			personSearchSource: {
				query: query.length >= 2 ? query : undefined,
				domains: selectedCompany?.domain ? [selectedCompany?.domain] : undefined,
			},
			organizationId: selectedCompany?.id,
		});

		return () => {
			request.abort();
		};
	}, [query, selectedCompany, fetchSearch, dispatch]);

	const addRecentSearch = useCallback((entry: RecentSearch) => {
		const name = entry.name.trim();

		if (!name) {
			return;
		}

		setLastSearches((prev) => {
			const next = pushRecentSearch(prev, { kind: entry.kind, name });

			persistRecentSearches(next);

			return next;
		});
	}, []);

	const clearRecentSearches = () => {
		setLastSearches([]);
		clearStoredRecentSearches();
	};

	const removeRecentSearch = (name: string) => {
		setLastSearches((prev) => {
			const next = prev.filter((s) => s.name !== name);

			persistRecentSearches(next);

			return next;
		});
	};

	const handleRecentSearchClick = (name: string) => {
		setSearch(name);
		setSearchValue(name);
		// Run the search immediately rather than waiting out the debounce.
		setQuery(name);
	};

	// Persist newly selected contacts (by prospect id) as recent searches.
	const handleSelectContactIds = (ids: number[]) => {
		const previouslySelected = new Set(selectedContactIds);

		for (const id of ids) {
			if (previouslySelected.has(id)) {
				continue;
			}

			const added = contactList.find((c) => c.prospect?.id === id);

			if (added?.prospect?.name) {
				addRecentSearch({ kind: "person", name: added.prospect.name });
			}
		}

		setSelectedContactIds(ids);
	};

	// Persist the typed query as a recent search once typing settles.
	useEffect(() => {
		const value = searchValue.trim();

		if (value.length < 2) {
			return;
		}

		const timer = setTimeout(() => {
			addRecentSearch({ kind: "query", name: value });
		}, 3000);

		return () => clearTimeout(timer);
	}, [searchValue, addRecentSearch]);

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		setSearch(value);
		setSearchValue(value);
	};

	const handleCompanyClick = (company: OrganizationGlobalSearchInfoDTO | null) => {
		if (!company) {
			return;
		}

		if (selectedCompany?.id === company.id) {
			dispatch(clearSelectedCompany());

			return;
		}

		const campaignMap = new Map<number, number>();

		for (const c of contactList) {
			const orgId = c.prospect?.organization?.id;

			if (!orgId) {
				continue;
			}

			if (isContactInCampaign(c)) {
				campaignMap.set(orgId, (campaignMap.get(orgId) ?? 0) + 1);
			}
		}

		setCompaniesSnapshot(organizations?.data ?? []);
		setContactsPerCompanySnapshot(contactsPerCompany);
		setCampaignCountsSnapshot(campaignMap);
		setLastSearch(search);
		setSearch("");
		setSearchValue("");
		// Drop any pending typed query so the company-scoped search isn't filtered by stale text.
		setQuery("");

		if (company.name) {
			addRecentSearch({ kind: "company", name: company.name });
		}

		dispatch(
			setSelectedCompany({
				id: company.id,
				name: company.name ?? "",
				domain: company.primaryDomain ?? null,
				logoUrl: company.logoUrl ?? null,
			})
		);
	};

	const toggleLinkedInItem = (person: IPersonSummary) => {
		const isSelected = selectedLinkedInItems.some((li) => li.companyEnrichId === person.id);

		if (isSelected) {
			setSelectedLinkedInItems(
				selectedLinkedInItems.filter((li) => li.companyEnrichId !== person.id)
			);

			return;
		}

		setSelectedLinkedInItems([
			...selectedLinkedInItems,
			{
				companyEnrichId: person.id,
				name: person.name,
				linkedinUrl: "",
				domain: resolveDisplayCompany(person)?.domain ?? selectedCompany?.domain ?? "",
			},
		]);

		if (person.name) {
			addRecentSearch({ kind: "person", name: person.name });
		}
	};

	const contactsSection = (
		<section>
			<div className="flex items-center  mb-3 w-full justify-between">
				<button
					className="flex gap-1 items-center "
					onClick={() => setIsContactsExpanded((p) => !p)}
				>
					<Icon
						className="text-gray-600 text-[10px]"
						icon={isContactsExpanded ? faChevronDown : faChevronRight}
					/>
					<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
						{selectedCompany?.name
							? `Your contacts at ${selectedCompany?.name}`
							: "Existing contacts"}
					</span>
					{!isSearching && (contactData?.data?.length ?? 0) > 0 && (
						<span className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-2 rounded-full">
							{contactData.data.length}
						</span>
					)}
				</button>
				{!singleSelect && (
					<button
						onClick={() => {
							if (isSelectedAllContacts) {
								const currentIds = new Set(
									contactList
										.map((c) => c.prospect?.id)
										.filter((id): id is number => id !== undefined)
								);

								setSelectedContactIds(selectedContactIds.filter((id) => !currentIds.has(id)));

								return;
							}

							const existingIds = new Set(selectedContactIds);
							const toAdd = contactList
								.map((c) => c.prospect?.id)
								.filter((id): id is number => id !== undefined && !existingIds.has(id));

							setSelectedContactIds([...selectedContactIds, ...toAdd]);
						}}
						className=" w-fit whitespace-nowrap text-blue-action text-[11px] hover:cursor-pointer hover:underline"
					>
						{isSelectedAllContacts ? "Deselect all" : "Select all"}
					</button>
				)}
			</div>

			{isContactsExpanded && (
				<>
					{contactData?.data?.length === 0 && (
						<p className="text-sm italic text-gray-500 py-1">{t("basics.noResults")}</p>
					)}
					<div className="space-y-4">
						{contactList.map((item, idx) => (
							<div
								key={item.id ?? idx}
								className="rounded-md border relative border-status-info-light overflow-hidden"
							>
								<ContactLeadCompact
									contact={item}
									selectedProspectIds={selectedContactIds}
									setSelectedProspectIds={handleSelectContactIds}
									onOpen={() => onContactNavigate(item)}
								/>
							</div>
						))}
					</div>
					{isSearching && !isLoadingSearch && (
						<div className="flex justify-center py-3">
							<Loader />{" "}
						</div>
					)}
				</>
			)}
		</section>
	);

	const linkedinSection = (
		<section>
			<div className="flex items-center  mb-3 w-full justify-between">
				<button
					className="flex gap-1 items-center "
					onClick={() => setIsLinkedInExpanded((p) => !p)}
				>
					<Icon
						className="text-gray-600 text-[10px]"
						icon={isLinkedInExpanded ? faChevronDown : faChevronRight}
					/>
					<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
						From LinkedIn
					</span>
					{!isSearching && totalLinkedIn > 0 && (
						<span className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-2 rounded-full">
							{totalLinkedIn}
						</span>
					)}
				</button>
				{!singleSelect && (
					<button
						onClick={() => {
							if (isSelectedAllLinkedIn) {
								const currentIds = new Set(linkedInResults.map((p) => p.id));

								setSelectedLinkedInItems(
									selectedLinkedInItems.filter((li) => !currentIds.has(li.companyEnrichId ?? -1))
								);

								return;
							}

							const existingIds = new Set(selectedLinkedInItems.map((li) => li.companyEnrichId));
							const toAdd = linkedInResults
								.filter((person) => !existingIds.has(person.id))
								.map((person) => ({
									companyEnrichId: person.id,
									name: person.name,
									linkedinUrl: "",
									domain: resolveDisplayCompany(person)?.domain ?? selectedCompany?.domain ?? "",
								}));

							setSelectedLinkedInItems([...selectedLinkedInItems, ...toAdd]);
						}}
						className=" w-fit whitespace-nowrap text-blue-action text-[11px] hover:cursor-pointer hover:underline"
					>
						{isSelectedAllLinkedIn ? "Deselect all" : "Select all"}
					</button>
				)}
			</div>

			{isLinkedInExpanded && (
				<>
					{isSearching && (
						<div className="flex justify-center py-4">
							<Loader />{" "}
						</div>
					)}
					{!isSearching &&
						linkedInResults.map((person, idx) => (
							<div
								key={person.id ?? idx}
								className="rounded-md border relative border-status-info-light mb-4 last:mb-0 [&_.lead-item]:rounded-lg [&_.lead-item>div]:p-3 [&_.lead-item>div]:border-0 [&_.lead-item>div]:shadow-sm [&_.lead-item>div]:hover:shadow-md"
							>
								<LinkedInPersonCard
									person={person}
									isSelected={selectedLinkedInItems.some((li) => li.companyEnrichId === person.id)}
									failedImages={failedImages}
									onImageError={(url) => setFailedImages((prev) => new Set([...prev, url]))}
									onToggle={() => toggleLinkedInItem(person)}
								/>
							</div>
						))}
					{!isSearching && linkedInResults.length === 0 && (
						<p className="text-xs italic text-gray-500 py-1">{t("basics.noResults")}</p>
					)}
				</>
			)}
		</section>
	);

	return (
		<div className="flex min-h-[320px] h-[480px] max-h-[480px]">
			{/* ── Left column ── */}
			<div className="w-[320px] shrink-0 border-r border-gray-300 flex flex-col">
				<div className="p-4 pb-2">
					<SearchField
						handleChange={handleSearchChange}
						inputRef={searchInputRef}
						handleReset={() => {
							setSearch("");
							setSearchValue("");
							setQuery("");
							dispatch(clearGlobalSearch());
						}}
						icon={<Icon className="text-gray-500" icon={faSearch} />}
						iconPosition="left"
						name="globalSearch"
						placeholder="Search by name..."
						value={search}
					/>
					<p className="text-xs text-gray-500 mt-2 leading-relaxed">
						Searches your contacts, your companies, and LinkedIn at the same time.
					</p>
				</div>

				{!isIdle && (
					<div className="px-4 pt-3 pb-4 overflow-y-auto bb-scrollbar-darker flex-1">
						<div className="flex items-center gap-2 mb-2">
							<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
								Companies
							</span>
							{!isSearching && !selectedCompany?.name && (
								<span className="text-[11px] font-semiboldtext-gray-700 bg-gray-100 px-2 rounded-full">
									{totalCompanies}
								</span>
							)}
						</div>

						{isSearching && !selectedCompany && (
							<div className="flex justify-center py-4">
								<Loader />{" "}
							</div>
						)}

						{(!isSearching || selectedCompany) &&
							companies.map((company) => {
								const isSelected = selectedCompany && selectedCompany.id === company.id;

								return (
									<button
										key={company.id}
										className={classNames(
											"w-full text-left rounded-xl border p-3 mb-2 transition-all hover:cursor-pointer",
											isSelected
												? "border-blue-lightest bg-action-200 hover:border-action"
												: "border-gray-200 bg-white hover:border-gray-400"
										)}
										onClick={() => handleCompanyClick(company || null)}
									>
										{/* ── Top row: logo + name + ✕ ── */}
										<div className="flex items-center gap-3">
											{company.logoUrl && !failedImages.has(company.logoUrl) ? (
												<img
													alt={company.name ?? ""}
													className="w-6 h-6 rounded object-contain border border-gray-300 shrink-0"
													src={company.logoUrl}
													onError={() =>
														setFailedImages((prev) => new Set([...prev, company.logoUrl!]))
													}
												/>
											) : (
												<div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center text-[10px] font-semibold text-gray-600 shrink-0">
													{company.name?.charAt(0)?.toUpperCase() || "?"}
												</div>
											)}

											<div className="flex flex-col min-w-0 flex-1">
												<span
													className={classNames(
														"text-sm font-medium truncate",
														isSelected ? "text-brand-black" : "text-gray-900"
													)}
												>
													{company.name}
												</span>
												<span className="text-xs font-light truncate text-gray-500">
													{company.primaryDomain}
												</span>
											</div>

											{isSelected && (
												<span className="shrink-0 text-brand-black hover:text-blue-action hover:bg-blue-action/10 p-1 text-xs leading-none rounded-xs">
													✕
												</span>
											)}
										</div>

										{/* ── Bottom row: contact/campaign badges ── */}
										{(() => {
											const counts = company?.name
												? displayedContactsPerCompany.get(company?.id)
												: null;

											if (!counts) {
												return null;
											}

											const countsinCampaign = selectedCompany
												? (campaignCountsSnapshot.get(company.id) ?? 0)
												: contactList.filter(
														(c) =>
															c.prospect?.organization?.id === company.id && isContactInCampaign(c)
													).length;

											return (
												<div className="flex flex-col gap-1.5 mt-2 pl-9">
													<span
														className={classNames(
															"inline-flex items-center gap-1 font-bold text-xs text-status-info px-2 py-0.5 rounded-full w-fit",
															isSelected ? "bg-brand-white" : "bg-status-info-light"
														)}
													>
														<Icon className="text-status-info text-[8px]" icon={faUser} />
														{counts} {counts === 1 ? "person" : "people"} in contacts
													</span>

													{countsinCampaign > 0 && (
														<span
															className={classNames(
																"inline-flex items-center font-bold gap-1 text-xs text-status-error px-2 py-0.5 rounded-full w-fit",
																isSelected ? "bg-brand-white" : "bg-status-error-light"
															)}
														>
															<Icon className="text-status-error text-[8px]" icon={faBolt} />
															{countsinCampaign} {countsinCampaign === 1 ? "person" : "people"} in
															campaign
														</span>
													)}
												</div>
											);
										})()}
									</button>
								);
							})}

						{!isSearching && companies.length === 0 && !selectedCompany && (
							<p className="text-xs italic text-gray-500 py-2">{t("basics.noResults")}</p>
						)}
					</div>
				)}
			</div>

			{/* ── Right column ── */}
			<div className="flex-1 flex flex-col overflow-hidden">
				{isIdle && (
					<div className="flex-1 flex flex-col items-center pt-4 gap-2 px-6 pb-8 text-center overflow-y-auto bb-scrollbar-darker">
						<div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
							<Icon className="text-gray-500 text-base" icon={faSearch} />
						</div>
						<p className="text-sm font-medium text-gray-700">Search for someone</p>
						<p className="text-xs text-gray-500 leading-relaxed w-[300px]">
							Type a name to search across your contacts and LinkedIn — both at the same time.
						</p>

						{lastSearches.length > 0 && (
							<div className="w-full max-w-[340px] mt-6 text-left">
								<div className="flex items-center justify-between mb-2">
									<span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
										<Icon className="text-gray-500 text-[10px]" icon={faClockRotateLeft} />
										Recent searches
									</span>
									<button
										onClick={clearRecentSearches}
										className="text-[11px] text-blue-action hover:cursor-pointer hover:underline"
									>
										Clear
									</button>
								</div>
								<div className="flex flex-col gap-0.5">
									{lastSearches.slice(0, MAX_VISIBLE_SEARCHES).map((item) => (
										<div
											key={`${item.kind}-${item.name}`}
											className="group flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-gray-100"
										>
											<button
												onClick={() => handleRecentSearchClick(item.name)}
												className="flex items-center gap-2 flex-1 min-w-0 text-left hover:cursor-pointer"
											>
												<Icon
													className="text-gray-500 text-xs shrink-0"
													icon={RECENT_SEARCH_ICONS[item.kind]}
												/>
												<span className="text-sm text-gray-700 truncate">{item.name}</span>
											</button>
											<button
												aria-label={`Remove ${item.name}`}
												onClick={() => removeRecentSearch(item.name)}
												className="shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-700 hover:cursor-pointer p-1 rounded"
											>
												<Icon className="text-xs" icon={faXmark} />
											</button>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				)}
				{selectedCompany?.name && (
					<div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-100 shrink-0">
						<div className="flex items-center gap-2 text-sm text-gray-600">
							{selectedCompany.logoUrl && !failedImages.has(selectedCompany.logoUrl) ? (
								<img
									alt={selectedCompany.name ?? ""}
									className="w-5 h-5 rounded object-contain border border-gray-300 shrink-0"
									src={selectedCompany.logoUrl}
									onError={() =>
										setFailedImages((prev) => new Set([...prev, selectedCompany.logoUrl!]))
									}
								/>
							) : (
								<div className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center text-[10px] font-semibold text-gray-600 shrink-0">
									{selectedCompany.name?.charAt(0)?.toUpperCase() || "?"}
								</div>
							)}
							Showing{" "}
							{search && (
								<>
									<span className="font-semibold text-gray-800">{search}</span> at{" "}
								</>
							)}
							<span className="font-semibold text-blue-600">{selectedCompany?.name}</span>
						</div>
						<Button
							title="Back to all results"
							className="w-fit! text-xs py-1! px-2! h-fit!"
							image={<Icon className="w-[10px] h-[10px] mr-2" icon={faArrowLeft} />}
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.M}
							onClick={() => {
								dispatch(clearSelectedCompany());
								setSearch(lastSearch);
								setSearchValue(lastSearch);
								// Restore the prior results immediately instead of waiting out the debounce.
								setQuery(lastSearch);
							}}
						/>
					</div>
				)}

				{isLoadingSearch || isDebouncePending ? (
					<div className="flex-1 overflow-y-auto bb-scrollbar-darker">
						<div className="flex items-center justify-center h-full">
							<Loader />
						</div>
					</div>
				) : (
					!isIdle && (
						<div className="flex-1 overflow-y-auto bb-scrollbar-darker">
							<div className="p-4 space-y-5">
								{selectedCompany ? (
									<>
										{contactsSection}
										{linkedinSection}
									</>
								) : (
									<>
										{linkedinSection}
										{contactsSection}
									</>
								)}
							</div>
						</div>
					)
				)}

				{selectedCompany?.domain && selectedCompany?.name && !isLoadingSearch && (
					<div className="shrink-0  px-2 py-3 flex items-center w-full justify-between bg-white">
						<span className="text-xs text-gray-700 w-full">
							Find more people at{" "}
							<span className="font-semibold text-gray-900">{selectedCompany?.name}</span>
						</span>
						<Button
							title="Search"
							image={<Icon icon={faSearch} />}
							className="w-fit! text-xs px-2! py-1! "
							size={ButtonSize.M}
							onClick={() =>
								onNavigateToFilters(
									selectedCompany?.domain && selectedCompany?.name
										? { name: selectedCompany.name, domain: selectedCompany.domain }
										: undefined
								)
							}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
